// The public pages' few interactive parts (ADR 0015: static HTML plus small islands). Each part runs
// only when its elements are on the page. Built by Vite (vite.config.ts, the "site" input).
import "./site.css";
import { beanSvg, colourFor } from "@obean/beans";
import { hand, setHand } from "../src/input/hand.js";
import { nextPath, passBack } from "./next-path.js";
import { dailyOf, dayOf } from "@obean/game-contract/daily";
import { diagnosticsTrace, readDiagnostics } from "../src/host/diagnostics.js";
import { badgesOf, loadBoard, playsLine } from "../src/net/board.js";
import { beansOf, levelOf, loadWallet } from "../src/loop/beans.js";
import { weekTable } from "./friends-week.js";
import { OBEAN_FEATS, loadFeats, progressOf } from "../src/loop/feats.js";
import { dailyStreak, playedDays, streakText } from "../src/solo/daily.js";
import { loadProgress } from "../src/solo/progress.js";
import type { YourObeanGame } from "./pages/account.js";

const $ = (id: string): HTMLElement | null => document.getElementById(id);
const input = (id: string) =>
  document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null;
const hashToken = () => location.hash.replace(/^#/, "");

// ── Contact: each topic first offers the fastest self-serve path, then the form (Sentry pattern).
const SELF: Record<string, [title: string, text: string, urgent?: boolean] | null> = {
  game: [
    "Tell us the game, the mission and the device",
    "A screenshot of the problem helps. Crash reports already reach us automatically, so you don't need to copy errors.",
  ],
  voice: [
    "Check the microphone first",
    "Allow the microphone when the browser asks, and make sure each of you turned voice on. The game keeps working without voice.",
  ],
  payment: [
    "Have your Stripe receipt ready",
    "Stripe emailed it when you bought the pack. Put the receipt number in your message and we can find the purchase straight away.",
  ],
  report: [
    "Fastest: use Report in the room",
    "It sends the right details with your report, and you can block people there too. If someone is in danger, contact local emergency services first.",
    true,
  ],
  privacy: [
    "Deleting your data is instant",
    "Choose Delete my data on the Make a room page (obean.com/new). For anything else about your data, send this form.",
  ],
  press: [
    "Hello!",
    "Tell us who you are and what you have in mind. Game makers: say a few words about your game.",
  ],
  other: null,
};

function contact(form: HTMLFormElement) {
  const topic = document.getElementById("topic") as HTMLSelectElement | null;
  const ss = $("ss");
  const err = $("err");
  const sent = $("sent");
  const ticket = $("ticket");
  if (!topic || !ss || !err || !sent || !ticket) return;
  const show = () => {
    const s = SELF[topic.value];
    ss.hidden = !s;
    if (!s) return;
    ss.className = s[2] ? "selfserve urgent" : "selfserve";
    const b = document.createElement("b");
    b.textContent = s[0];
    const p = document.createElement("p");
    p.textContent = s[1];
    ss.replaceChildren(b, p);
  };
  if (hashToken() in SELF) topic.value = hashToken();
  topic.addEventListener("change", show);
  show();
  // What this tab recorded of its last play, offered (unticked) to attach (P3-OBS-06).
  let kept: ReturnType<typeof readDiagnostics> = null;
  try {
    kept = readDiagnostics(sessionStorage);
  } catch {
    // No storage here: nothing to offer.
  }
  const diag = $("diag");
  if (diag && kept) diag.hidden = false;

  const fail = (el: HTMLElement | null, message: string) => {
    err.textContent = message;
    err.hidden = false;
    if (el) {
      el.setAttribute("aria-invalid", "true");
      el.focus();
    }
  };
  const submit = async () => {
    const message = (input("msg")?.value ?? "").trim();
    const email = (input("em")?.value ?? "").trim();
    form.querySelectorAll("[aria-invalid]").forEach((el) => {
      el.removeAttribute("aria-invalid");
    });
    err.hidden = true;
    if (!topic.value) {
      fail(topic, "Choose what it's about.");
      return;
    }
    if (message.length < 10) {
      fail($("msg"), "Write a little more: at least 10 characters.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      fail($("em"), "That email doesn't look right.");
      return;
    }
    const button = form.querySelector("button");
    if (button) button.disabled = true;
    try {
      const res = await fetch("/api/support", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          topic: topic.value,
          message,
          email,
          website: input("website")?.value ?? "",
        }),
      });
      if (res.status === 201) {
        const { id } = (await res.json()) as { id: string };
        if (kept && (input("attach") as HTMLInputElement | null)?.checked)
          void fetch("/api/traces", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(diagnosticsTrace(kept, id)),
          }).catch(() => {
            // The message got through; the diagnostics are a bonus.
          });
        ticket.textContent = id;
        form.hidden = true;
        sent.hidden = false;
        sent.focus();
      } else if (res.status === 429) {
        fail(null, "Too many messages from here just now. Try again in a minute.");
      } else {
        const body = (await res.json().catch(() => ({}))) as { field?: string };
        if (body.field === "email") fail($("em"), "That email doesn't look right.");
        else if (body.field === "message")
          fail($("msg"), "The message must be between 10 and 4000 characters.");
        else fail(null, "That didn't go through. Try again, or email hello@obean.com.");
      }
    } catch {
      fail(null, "No connection. Check your internet and try again.");
    } finally {
      if (button) button.disabled = false;
    }
  };
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    void submit();
  });
}

// ── Help: filter answers as you type.
function help(q: HTMLInputElement) {
  const cats = $("cats");
  const nohit = $("nohit");
  q.addEventListener("input", () => {
    const term = q.value.trim().toLowerCase();
    let hits = 0;
    document.querySelectorAll<HTMLDetailsElement>("[data-q]").forEach((d) => {
      const on = !term || (d.dataset["q"] ?? "").includes(term);
      d.hidden = !on;
      if (on) hits++;
      if (term && on) d.open = true;
    });
    document.querySelectorAll<HTMLElement>("[data-help]").forEach((s) => {
      s.hidden = !s.querySelector("[data-q]:not([hidden])");
    });
    if (cats) cats.hidden = term !== "";
    if (nohit) nohit.hidden = hits > 0;
  });
}

// ── Status: the last 30 days from /api/status (apps/api/src/status.ts).
type DayState = "ok" | "degraded" | "none";
interface StatusBody {
  from: number;
  components: { id: string; days: DayState[] }[];
}
const DAY_LABEL: Record<DayState, string> = {
  ok: "working",
  degraded: "slow or failing",
  none: "quiet, nothing failed",
};

function status(sum: HTMLElement) {
  const text = $("sum-text");
  if (!text) return;
  fetch("/api/status")
    .then((r) =>
      r.ok ? (r.json() as Promise<StatusBody>) : Promise.reject(new Error(String(r.status))),
    )
    .then((body) => {
      const slowToday: string[] = [];
      for (const c of body.components) {
        const el = document.querySelector<HTMLElement>(`[data-comp="${c.id}"]`);
        if (!el) continue;
        const label = el.querySelector("b")?.textContent ?? c.id;
        const bars = el.querySelector<HTMLElement>(".bars");
        const state = el.querySelector<HTMLElement>("[data-state]");
        // Not Array.at(): it needs Safari 15.4 and Chrome 92, above the browser floor.
        const today = c.days[c.days.length - 1] ?? "none";
        if (today === "degraded") slowToday.push(label);
        if (state) {
          state.textContent = today === "degraded" ? "Slow today" : "Working";
          state.className = today === "degraded" ? "degraded" : "";
        }
        if (bars) {
          bars.replaceChildren(
            ...c.days.map((d, i) => {
              const bar = document.createElement("i");
              if (d !== "ok") bar.className = d === "degraded" ? "warn" : "none";
              bar.title = `${new Date(body.from + i * 86_400_000).toISOString().slice(0, 10)}: ${DAY_LABEL[d]}`;
              return bar;
            }),
          );
          const bad = c.days.filter((d) => d === "degraded").length;
          bars.setAttribute(
            "aria-label",
            `${label}: ${bad === 0 ? "working on all of the last 30 days" : `slow on ${String(bad)} of the last 30 days`}`,
          );
        }
      }
      sum.className = slowToday.length ? "status-sum degraded" : "status-sum";
      text.textContent = slowToday.length
        ? `Slow right now: ${slowToday.join(", ")}`
        : "Everything is working";
    })
    .catch(() => {
      sum.className = "status-sum unknown";
      text.textContent = "Couldn't load the latest checks. Try again in a minute.";
    });
}

// ── Sign in (ADR 0016): show the providers this environment has; start the provider's flow.
interface Me {
  nickname: string | null;
  /** Signed in: the person's id, whose record in this browser Your Obean shows. */
  personId?: string;
  packs: string[];
  signedIn: boolean;
  /** Whether the Obean Pass is sold here (ADR 0022's switch), and theirs if they have it. */
  passOffered?: boolean;
  /** plan "free": a period Obean gave, with nothing to manage in Stripe (#384). */
  pass?: { plan: "month" | "year" | "free"; periodEnd: number | null; cancelling: boolean } | null;
}
/** Who this browser is, asked once per page and shared (the header and /me both want it). */
let meOnce: Promise<Me | null> | null = null;
const getMe = (): Promise<Me | null> =>
  // `optional`: no guest yet is a plain null, not a 401 the browser would log as an error.
  (meOnce ??= fetch("/api/me?optional=1")
    .then(async (res) => (res.ok ? ((await res.json()) as Me) : null))
    .catch((e: unknown) => {
      meOnce = null; // a network blip: the next caller asks again
      throw e;
    }));

// ── The header's account link: "Account" (to /me) while this browser is signed in. Only a browser
// that has signed in asks, so first-time visitors make no request. The flag is a convenience, not
// a credential: the session cookie (HttpOnly) decides, and a stale flag is cleared.
const SIGNED_IN = "obean.signedIn";
const remember = (on: boolean) => {
  try {
    if (on) localStorage.setItem(SIGNED_IN, "1");
    else localStorage.removeItem(SIGNED_IN);
  } catch {
    // storage blocked: the link just stays "Sign in"
  }
};
const showAccount = () => {
  const link = $("nav-account");
  if (!(link instanceof HTMLAnchorElement)) return;
  link.href = "/me";
  link.textContent = "Account";
};
function accountLink() {
  const flagged = (() => {
    try {
      return localStorage.getItem(SIGNED_IN) === "1";
    } catch {
      return false; // storage blocked: the link just stays "Sign in"
    }
  })();
  if (!flagged) return;
  void getMe().then(
    (m) => {
      if (m?.signedIn) showAccount();
      else remember(false);
    },
    () => undefined,
  );
}

function signIn(box: HTMLElement) {
  const err = $("signin-err");
  const next = nextPath(location.search);
  const passkeyButton = $("passkey-signin") as HTMLButtonElement | null;
  if (passkeyButton) {
    if (!passkeysWork()) passkeyButton.hidden = true;
    passkeyButton.addEventListener("click", () => {
      void passkeySignIn(next, (m) => {
        if (!err) return;
        err.textContent = m;
        err.hidden = false;
      });
    });
  }
  const qrButton = $("qr-start") as HTMLButtonElement | null;
  if (qrButton) qrSignIn(qrButton, next);
  const show = (message: string) => {
    if (!err) return;
    err.textContent = message;
    err.hidden = false;
  };
  void getMe()
    .then((me) => {
      if (me?.signedIn) {
        remember(true);
        location.replace(next);
      }
    })
    .catch(() => undefined);
  void fetch("/api/sign-in-options")
    .then((r) => r.json() as Promise<{ providers: string[] }>)
    .then(({ providers }) => {
      box.querySelectorAll<HTMLButtonElement>("[data-provider]").forEach((b) => {
        b.hidden = !providers.includes(b.dataset["provider"] ?? "");
      });
      const none = $("signin-none");
      if (none) none.hidden = providers.length > 0;
    })
    .catch(() => {
      show("Couldn't reach Obean. Check your connection and try again.");
    });
  box.querySelectorAll<HTMLButtonElement>("[data-provider]").forEach((b) => {
    b.addEventListener("click", () => {
      b.disabled = true;
      void fetch("/api/auth/sign-in/social", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ provider: b.dataset["provider"], callbackURL: next }),
      })
        .then(async (res) => {
          const body = (await res.json().catch(() => ({}))) as { url?: string };
          if (res.ok && body.url) location.assign(body.url);
          else show("That didn't work. Try again, or play as a guest.");
        })
        .catch(() => {
          show("Couldn't reach Obean. Check your connection and try again.");
        })
        .finally(() => {
          b.disabled = false;
        });
    });
  });
}

/** "Your Pass renews on 14 November", in the reader's language. */
function passLine(pass: NonNullable<Me["pass"]>): string {
  const plan = pass.plan === "year" ? "yearly" : "monthly";
  if (pass.periodEnd === null) return `You have Obean Pass (${plan}).`;
  const day = new Date(pass.periodEnd).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
  });
  if (pass.plan === "free") return `You have Obean Pass free, from Obean, until ${day}.`;
  return pass.cancelling
    ? `You have Obean Pass until ${day}. It won't renew.`
    : `You have Obean Pass (${plan}). It renews on ${day}.`;
}

/** Stripe's customer portal for the signed-in person's Pass, coming back to `back`. */
async function managePass(back: string): Promise<string> {
  const res = await fetch("/api/pass/manage", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ back }),
  });
  const body = (await res.json().catch(() => ({}))) as { url?: string };
  if (!res.ok || !body.url) throw new Error(String(res.status));
  return body.url;
}

// ── You: who you are here, your Obean Pass, sign out and delete.
function me(root: HTMLElement) {
  const err = $("me-err");
  const fail = (message: string) => {
    if (!err) return;
    err.textContent = message;
    err.hidden = false;
  };
  const sub = $("me-sub");
  void getMe()
    .then((m) => {
      const title = $("me-title");
      if (!m?.signedIn) {
        remember(false);
        if (sub) sub.textContent = "Not signed in.";
        const out = $("me-signed-out");
        if (out) out.hidden = false;
        return;
      }
      remember(true);
      showAccount();
      const actions = $("me-head-actions");
      if (actions) actions.hidden = false;
      if (title) title.textContent = m.nickname ? `Hi, ${m.nickname}` : "Hi";
      if (sub) sub.textContent = "Signed in. Your Pass and friends follow you to every device.";
      const state = $("me-pass-state");
      const manage = $("me-pass-manage");
      const get = $("me-pass-get");
      if (m.pass) {
        if (state) state.textContent = passLine(m.pass);
        // A free period has nothing in Stripe to manage.
        if (manage) manage.hidden = m.pass.plan === "free";
      } else if (m.passOffered) {
        if (state) state.textContent = "You don't have Obean Pass.";
        if (get) get.hidden = false;
      }
      const inside = $("me-signed-in");
      if (inside) inside.hidden = false;
    })
    .catch(() => {
      fail("Couldn't load your account. Try again in a minute.");
    });
  $("me-signout")?.addEventListener("click", () => {
    void fetch("/api/auth/sign-out", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
    })
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        remember(false);
        location.assign("/");
      })
      .catch(() => {
        fail("Couldn't sign out. Check your connection and try again.");
      });
  });
  $("me-pass-manage")?.addEventListener("click", () => {
    managePass("/me").then(
      (url) => {
        location.assign(url);
      },
      () => {
        fail("Couldn't open your Pass settings. Try again in a minute.");
      },
    );
  });
  const confirm = $("me-confirm");
  $("me-delete")?.addEventListener("click", () => {
    if (confirm) confirm.hidden = false;
  });
  $("me-delete-no")?.addEventListener("click", () => {
    if (confirm) confirm.hidden = true;
  });
  $("me-delete-yes")?.addEventListener("click", () => {
    void fetch("/api/me", { method: "DELETE" })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          if (body.error === "pass_not_cancelled")
            throw new Error(
              "Your Obean Pass couldn't be stopped just now, so nothing was deleted. Try again in a minute.",
            );
          throw new Error(String(res.status));
        }
        remember(false);
        root.replaceChildren(
          Object.assign(document.createElement("p"), {
            className: "page-pad lead",
            textContent: "Deleted. Everything about you is gone from Obean.",
          }),
        );
      })
      .catch((e: unknown) => {
        const said = e instanceof Error && e.message.includes(" ") ? e.message : null;
        fail(
          said ?? "Couldn't delete right now. Try again in a minute, or write to hello@obean.com.",
        );
      });
  });
}

// ── Obean Pass (ADR 0022): the plans for a signed-in person, sign-in first otherwise; checkout
// at Stripe, coming back here (or to `?back=`, e.g. the room they came from).
function pass() {
  const back = passBack(location.search);
  const note = $("pass-note");
  const err = $("pass-err");
  const say = (el: HTMLElement | null, text: string) => {
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
  };
  const returned = new URLSearchParams(location.search).get("pass");
  if (returned === "done") say(note, "Welcome to Obean Pass! It can take a moment to show up.");
  if (returned === "cancelled") say(note, "Checkout cancelled: nothing was charged.");
  const signin = $("pass-signin");
  if (signin instanceof HTMLElement) {
    const link = signin.querySelector("a");
    if (link) link.href = `/signin?next=${encodeURIComponent(location.pathname + location.search)}`;
  }
  void getMe().then(
    (m) => {
      if (!m?.passOffered) return; // the beta note stays
      const off = $("pass-off");
      if (off) off.hidden = true;
      const show = m.pass ? "pass-have" : m.signedIn ? "pass-buy" : "pass-signin";
      const box = $(show);
      if (box) box.hidden = false;
    },
    () => undefined,
  );
  document.querySelectorAll<HTMLButtonElement>("#pass-buy [data-plan]").forEach((b) => {
    b.addEventListener("click", () => {
      b.disabled = true;
      if (err) err.hidden = true;
      void fetch("/api/pass/checkout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ plan: b.dataset["plan"], ...(back ? { back } : {}) }),
      })
        .then(async (res) => {
          const body = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
          if (res.ok && body.url) {
            location.assign(body.url);
            return;
          }
          say(
            err,
            body.error === "already_member"
              ? "You already have the Pass."
              : body.error === "sign_in_required"
                ? "Sign in first: the Pass belongs to you, not to this browser."
                : "The Pass can't be bought right now. Try again later.",
          );
        })
        .catch(() => {
          say(err, "Couldn't reach Obean. Check your connection and try again.");
        })
        .finally(() => {
          b.disabled = false;
        });
    });
  });
}

// ── Touch controls: which thumb taps the buttons (src/input/hand.ts), kept on this device.
function handPicker(box: HTMLElement) {
  const current = hand();
  const state = $("hand-state");
  box.querySelectorAll<HTMLInputElement>('input[name="hand"]').forEach((input) => {
    input.checked = input.value === current;
    input.addEventListener("change", () => {
      if (!input.checked) return;
      setHand(input.value === "left" ? "left" : "right");
      if (state)
        state.textContent =
          input.value === "left"
            ? "Saved: buttons on the left, steering on the right. Kept on this device."
            : "Saved: buttons on the right, steering on the left. Kept on this device.";
    });
  });
}

// ── Friends (P3-12): friends with Play, requests to answer, people to ask.
interface Person {
  id: string;
  name: string;
  lastPlayedAt: number | null;
  /** Their bean's extra (ADR 0018). */
  bean?: string | null;
  /** Friends only (ADR 0031): online now, the game they're in, and my private star. */
  online?: boolean;
  game?: string | null;
  favourite?: boolean;
  /** Friends only (ADR 0043 part 5): their beans and this week's; null while they appear offline. */
  beans?: number | null;
  week?: number | null;
}
interface Friends {
  friends: Person[];
  incoming: Person[];
  outgoing: Person[];
  recent: Person[];
  /** My beans and this week's, from my synced devices. */
  mine?: { beans: number; week: number };
}

const ago = (ms: number | null) => {
  if (ms === null) return "";
  const days = Math.floor((Date.now() - ms) / 86_400_000);
  return days < 1
    ? "Played today"
    : days === 1
      ? "Played yesterday"
      : `Played ${String(days)} days ago`;
};

function friends() {
  const act = (method: string, path: string, body?: object) =>
    fetch(path, {
      method,
      headers: body ? { "content-type": "application/json" } : {},
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  const button = (label: string, cls: string, onClick: () => Promise<unknown>) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = `btn btn-sm ${cls}`;
    b.textContent = label;
    b.addEventListener("click", () => {
      b.disabled = true;
      void onClick().then(load, load);
    });
    return b;
  };
  const row = (p: Person, ...actions: HTMLElement[]) => {
    const li = document.createElement("li");
    // Their bean: SVG built from @obean/beans' constants only, so it's safe as markup.
    const bean = Object.assign(document.createElement("span"), { className: "bean" });
    bean.setAttribute("aria-hidden", "true");
    bean.innerHTML = beanSvg({ colour: colourFor(p.id), extra: p.bean ?? null, size: 36 });
    li.append(bean);
    const who = document.createElement("span");
    const name = document.createElement("b");
    name.textContent = p.name;
    who.append(name);
    if (typeof p.beans === "number")
      who.append(
        Object.assign(document.createElement("span"), {
          className: "level",
          textContent: ` · Level ${String(levelOf(p.beans).level)}`,
        }),
      );
    if (p.online === true)
      who.append(
        Object.assign(document.createElement("span"), {
          className: "when online",
          textContent: p.game ? `In ${gameNames.get(p.game) ?? p.game}` : "Online",
        }),
      );
    else if (p.lastPlayedAt !== null)
      who.append(
        Object.assign(document.createElement("span"), {
          className: "when",
          textContent: ago(p.lastPlayedAt),
        }),
      );
    const box = Object.assign(document.createElement("span"), { className: "actions" });
    box.append(...actions);
    li.append(who, box);
    return li;
  };
  const fill = (id: string, rows: HTMLLIElement[], boxId?: string) => {
    $(id)?.replaceChildren(...rows);
    if (boxId) {
      const box = $(boxId);
      if (box) box.hidden = rows.length === 0;
    }
  };
  /** Play with an online friend: a new room, and the in-app invite to it (ADR 0031). */
  const playWith = async (p: Person) => {
    const made = await act("POST", "/api/rooms");
    if (!made.ok) {
      location.href = "/new";
      return;
    }
    const { roomId } = (await made.json()) as { roomId: string };
    await act("POST", `/api/friends/${p.id}/invite`, { roomId, source: "friends" });
    location.href = `/r/${roomId}`;
  };
  const star = (p: Person) => {
    const b = button(p.favourite === true ? "★" : "☆", "btn-ghost star", () =>
      act(p.favourite === true ? "DELETE" : "PUT", `/api/friends/${p.id}/favourite`),
    );
    b.setAttribute("aria-pressed", String(p.favourite === true));
    b.setAttribute("aria-label", `${p.favourite === true ? "Unstar" : "Star"} ${p.name}`);
    return b;
  };
  async function load() {
    const res = await fetch("/api/friends");
    if (!res.ok) return;
    const f = (await res.json()) as Friends;
    fill(
      "friends-list",
      f.friends.map((p) =>
        row(
          p,
          star(p),
          p.online === true
            ? button("Play", "btn-p1", () => playWith(p))
            : Object.assign(document.createElement("a"), {
                className: "btn btn-p1 btn-sm",
                href: "/new",
                textContent: "Play",
              }),
          button("Remove", "btn-ghost", () => act("DELETE", `/api/friends/${p.id}`)),
        ),
      ),
    );
    fill(
      "friends-incoming",
      f.incoming.map((p) =>
        row(
          p,
          button("Accept", "btn-p1", () => act("POST", `/api/friends/requests/${p.id}/accept`)),
          button("Ignore", "btn-ghost", () => act("DELETE", `/api/friends/requests/${p.id}`)),
        ),
      ),
      "friends-incoming-box",
    );
    const asked = new Set(f.outgoing.map((p) => p.id));
    fill(
      "friends-recent",
      [...f.outgoing, ...f.recent.filter((p) => !asked.has(p.id))].map((p) =>
        asked.has(p.id)
          ? row(
              p,
              Object.assign(document.createElement("span"), {
                className: "dim small",
                textContent: "Asked",
              }),
            )
          : row(
              p,
              button("Add friend", "btn-ghost", () =>
                act("POST", "/api/friends/requests", { to: p.id }),
              ),
            ),
      ),
      "friends-recent-box",
    );
    const empty = $("friends-empty");
    if (empty)
      empty.hidden = f.friends.length + f.incoming.length + f.outgoing.length + f.recent.length > 0;
    const week = weekTable(f);
    $("friends-week")?.replaceChildren(
      ...week.map((r) => {
        const li = document.createElement("li");
        if (r.me) li.className = "mine";
        const who = document.createElement("span");
        who.textContent = r.name;
        const n = Object.assign(document.createElement("span"), {
          className: "dim",
          textContent: `${r.week.toLocaleString("en")} ${r.week === 1 ? "bean" : "beans"}`,
        });
        li.append(who, " ", n);
        return li;
      }),
    );
    const weekBox = $("friends-week-box");
    if (weekBox) weekBox.hidden = week.length === 0;
  }
  // Game names for "In Beanpair" (the catalog is cached for a minute).
  void fetch("/api/catalog")
    .then((r) => r.json() as Promise<{ games: { manifest: { id: string; name: string } }[] }>)
    .then((c) => {
      for (const g of c.games) gameNames.set(g.manifest.id, g.manifest.name);
    })
    .catch(() => undefined)
    .finally(() => void load());
  // Refresh who's online while the page is open (ADR 0031: about a minute late at most).
  setInterval(() => {
    if (document.visibilityState === "visible") void load();
  }, 30_000);

  // Appear offline: the switch reads "Show friends when I'm online".
  const visible = $("presence-visible");
  if (visible instanceof HTMLInputElement) {
    void fetch("/api/presence")
      .then((r) => (r.ok ? (r.json() as Promise<{ hidden: boolean }>) : null))
      .then((p) => {
        if (p) visible.checked = !p.hidden;
      });
    visible.addEventListener("change", () => {
      void act("PUT", "/api/presence", { hidden: !visible.checked });
    });
  }
}
const gameNames = new Map<string, string>();

if ($("friends")) friends();
// ── Passkeys (ADR 0016). The WebAuthn helper loads only when a passkey button is used.
const passkeysWork = () => typeof window.PublicKeyCredential === "function";
const postJson = (path: string, body: object) =>
  fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

async function passkeySignIn(next: string, fail: (m: string) => void) {
  try {
    const { startAuthentication } = await import("@simplewebauthn/browser");
    const options = (await (
      await fetch("/api/auth/passkey/generate-authenticate-options")
    ).json()) as Parameters<typeof startAuthentication>[0]["optionsJSON"];
    const response = await startAuthentication({ optionsJSON: options });
    const res = await postJson("/api/auth/passkey/verify-authentication", { response });
    if (res.ok) {
      remember(true);
      location.assign(next);
    } else
      fail("That passkey isn't one Obean knows. Sign in another way, then add it on the You page.");
  } catch {
    fail("No passkey was used. Try again, or sign in another way.");
  }
}

function passkeyAdd(button: HTMLButtonElement) {
  const state = $("passkey-state");
  const say = (m: string) => {
    if (state) state.textContent = m;
  };
  if (!passkeysWork()) {
    button.hidden = true;
    say("This browser can't use passkeys.");
    return;
  }
  button.addEventListener("click", () => {
    button.disabled = true;
    void (async () => {
      try {
        const { startRegistration } = await import("@simplewebauthn/browser");
        const res0 = await fetch("/api/auth/passkey/generate-register-options");
        if (!res0.ok) {
          say("Sign in again first, then add the passkey.");
          return;
        }
        const options = (await res0.json()) as Parameters<
          typeof startRegistration
        >[0]["optionsJSON"];
        const response = await startRegistration({ optionsJSON: options });
        const res = await postJson("/api/auth/passkey/verify-registration", {
          response,
          name: "Added on obean.com",
        });
        say(
          res.ok
            ? "Passkey added. Next time, choose Use a passkey."
            : "That didn't work. Try again.",
        );
      } catch {
        say("No passkey was added.");
      } finally {
        button.disabled = false;
      }
    })();
  });
}

// ── QR sign-in (P3-13): show a code for a signed-in phone to approve, and wait.
interface DeviceCode {
  device_code: string;
  user_code: string;
  interval: number;
  expires_in: number;
}
const DEVICE_GRANT = "urn:ietf:params:oauth:grant-type:device_code";

function qrSignIn(start: HTMLButtonElement, next: string) {
  const area = $("qr");
  const state = $("qr-state");
  const say = (m: string) => {
    if (state) state.textContent = m;
  };
  let timer = 0;
  start.addEventListener("click", () => {
    start.disabled = true;
    window.clearTimeout(timer);
    void (async () => {
      try {
        const res = await postJson("/api/auth/device/code", { client_id: "obean-web" });
        if (!res.ok) throw new Error(String(res.status));
        const code = (await res.json()) as DeviceCode;
        const link = `${location.origin}/device?user_code=${encodeURIComponent(code.user_code)}`;
        const { renderSVG } = await import("uqr");
        const box = $("qr-code");
        if (box) box.innerHTML = renderSVG(link, { border: 1 });
        const shown = $("qr-user-code");
        if (shown) shown.textContent = code.user_code;
        if (area) area.hidden = false;
        say("Waiting for your phone…");
        let every = code.interval * 1000;
        const until = Date.now() + code.expires_in * 1000;
        const poll = async () => {
          if (Date.now() > until) {
            say("The code expired. Press the button for a new one.");
            start.disabled = false;
            return;
          }
          const r = await postJson("/api/auth/device/token", {
            grant_type: DEVICE_GRANT,
            device_code: code.device_code,
            client_id: "obean-web",
          });
          const body = (await r.json().catch(() => ({}))) as {
            access_token?: string;
            error?: string;
          };
          if (body.access_token) {
            say("Approved. Signing you in…");
            const set = await postJson("/api/sign-in/device-session", { token: body.access_token });
            if (set.ok) {
              remember(true);
              location.assign(next);
            } else say("That didn't work. Press the button to try again.");
            start.disabled = false;
            return;
          }
          if (body.error === "access_denied") {
            say("Your phone said no. Press the button to try again.");
            start.disabled = false;
            return;
          }
          if (body.error === "expired_token") {
            say("The code expired. Press the button for a new one.");
            start.disabled = false;
            return;
          }
          if (body.error === "slow_down") every += 1000;
          timer = window.setTimeout(() => void poll(), every);
        };
        timer = window.setTimeout(() => void poll(), every);
      } catch {
        say("Couldn't make a code. Check your connection and try again.");
        if (area) area.hidden = false;
        start.disabled = false;
      }
    })();
  });
}

// ── The phone's side: approve the code (it opens from the QR with ?user_code=).
function device() {
  const sub = $("device-sub");
  const err = $("device-err");
  const fail = (m: string) => {
    if (!err) return;
    err.textContent = m;
    err.hidden = false;
  };
  const ask = $("device-ask");
  const enter = document.getElementById("device-enter") as HTMLFormElement | null;
  const showCode = $("device-code");
  const begin = async (userCode: string) => {
    if (enter) enter.hidden = true;
    const me = await getMe().catch(() => null);
    if (!me?.signedIn) {
      if (sub) sub.textContent = "Sign in on this phone first, then come back to approve.";
      const link = Object.assign(document.createElement("a"), {
        className: "btn btn-p1",
        href: `/signin?next=${encodeURIComponent(`/device?user_code=${userCode}`)}`,
        textContent: "Sign in",
      });
      sub?.after(link);
      return;
    }
    const claim = await fetch(`/api/auth/device?user_code=${encodeURIComponent(userCode)}`);
    if (!claim.ok) {
      if (sub)
        sub.textContent = "That code isn't valid any more. Make a new one on the other device.";
      return;
    }
    if (sub) sub.textContent = "Another device wants to sign in as you.";
    if (showCode) showCode.textContent = userCode;
    if (ask) ask.hidden = false;
    const answer = async (path: string, done: string) => {
      const res = await postJson(path, { userCode });
      if (!res.ok) {
        fail("That didn't work. Make a new code on the other device and try again.");
        return;
      }
      if (ask) ask.hidden = true;
      if (sub) sub.textContent = done;
    };
    $("device-yes")?.addEventListener(
      "click",
      () => void answer("/api/auth/device/approve", "Done. Your other device is signed in."),
    );
    $("device-no")?.addEventListener(
      "click",
      () => void answer("/api/auth/device/deny", "Declined. Nothing was signed in."),
    );
  };
  const given = new URLSearchParams(location.search).get("user_code");
  if (given) {
    void begin(given);
  } else if (enter) {
    if (sub) sub.textContent = "Type the code shown on the other device.";
    enter.hidden = false;
    enter.addEventListener("submit", (e) => {
      e.preventDefault();
      const value =
        (document.getElementById("device-input") as HTMLInputElement | null)?.value
          .trim()
          .toUpperCase() ?? "";
      if (value) void begin(value);
    });
  }
}

// ── Your Obean (ADR 0043, P5-LOOP-03): level, beans, each game's progress and the achievements,
// from what this browser keeps for this person (the guest's, or the signed-in person's).
function yourObean() {
  let games: YourObeanGame[] = [];
  try {
    games = JSON.parse($("yo-data")?.textContent ?? "[]") as YourObeanGame[];
  } catch {
    return;
  }
  const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text !== undefined) e.textContent = text;
    return e;
  };
  const feat = (name: string, about: string, have: number, at: number, earned: boolean) => {
    const li = el("li", earned ? "yo-feat earned" : "yo-feat");
    li.append(el("span", "yo-mark", earned ? "★" : "☆"), el("b", undefined, name), " ");
    // A one-step achievement ("Win a mission") needs no count.
    const count = earned || at <= 1 ? "" : ` · ${fmt(Math.min(have, at))} of ${fmt(at)}`;
    li.append(el("span", "dim", `${about}${count}`));
    return li;
  };
  const fmt = (n: number) => Math.floor(n).toLocaleString("en-GB");
  const draw = (profile: string | null) => {
    const wallet = loadWallet(profile);
    const feats = loadFeats(profile);
    const total = beansOf(wallet);
    const lv = levelOf(total);
    const level = $("yo-level");
    if (total > 0 || feats.games.length > 0) {
      if (level) level.textContent = `Level ${String(lv.level)} · ${fmt(total)} beans`;
      const bar = $("yo-bar");
      const progress = $("yo-progress") as HTMLProgressElement | null;
      if (progress) {
        progress.max = lv.need;
        progress.value = lv.into;
      }
      const next = $("yo-next");
      if (next) next.textContent = `${fmt(lv.need - lv.into)} to level ${String(lv.level + 1)}`;
      if (bar) bar.hidden = false;
    }
    // The daily streak (ADR 0041), from two days in a row.
    const streak = $("yo-streak");
    const days = streakText(dailyStreak(playedDays(), dayOf(Date.now())));
    if (streak && days) {
      streak.textContent = `Daily streak: ${days}.`;
      streak.hidden = false;
    }
    const friends = $("yo-friends");
    if (friends && feats.together > 0) {
      friends.textContent = `Played with friends ${fmt(feats.together)} ${feats.together === 1 ? "time" : "times"}.`;
      friends.hidden = false;
    }
    const ctx = { level: lv.level, games: games.map((g) => g.id) };
    let earnedCount = 0;
    let allCount = 0;
    const obean = $("yo-obean");
    obean?.replaceChildren(
      ...OBEAN_FEATS.map((o) => {
        const p = o.progress(feats, ctx);
        const got = feats.earned[`obean:${o.id}`] !== undefined;
        allCount++;
        if (got) earnedCount++;
        return feat(o.name, o.about, p.have, p.at, got);
      }),
    );
    const list = $("yo-games");
    const cards = games.map((g) => {
      const won = new Set([
        ...Object.keys(feats.won[g.id] ?? {}),
        ...Object.keys(loadProgress(g.id, undefined, profile).won),
      ]);
      const card = el("div", "yo-game");
      const head = el("p", "yo-game-head");
      head.append(el("b", undefined, g.name), " ");
      head.append(
        el(
          "span",
          "dim",
          `${fmt(won.size)} of ${fmt(g.missions)} ${g.missions === 1 ? "mission" : "missions"} won`,
        ),
      );
      const ul = el("ul", "yo-feats");
      for (const a of g.achievements) {
        const p = progressOf(feats, g.id, a);
        const got = feats.earned[`${g.id}:${a.id}`] !== undefined;
        allCount++;
        if (got) earnedCount++;
        ul.append(feat(a.name, a.about, p.have, p.at, got));
      }
      card.append(head, ul);
      return { card, played: feats.games.includes(g.id) || won.size > 0 };
    });
    // The games played come first; the rest fold into one line, so the page stays short.
    const played = cards.filter((c) => c.played).map((c) => c.card);
    const rest = cards.filter((c) => !c.played).map((c) => c.card);
    const more = el("details", "yo-more");
    more.append(
      el(
        "summary",
        undefined,
        played.length === 0
          ? `Every game's achievements (${String(rest.length)})`
          : `${String(rest.length)} ${rest.length === 1 ? "game" : "games"} still to try`,
      ),
      ...rest,
    );
    list?.replaceChildren(...played, ...(rest.length > 0 ? [more] : []));
    const count = $("yo-count");
    if (count) count.textContent = `${String(earnedCount)} of ${String(allCount)}`;
  };
  draw(null);
  // Signed in: the person's record, not the guest's.
  void getMe()
    .then((m) => {
      if (m?.signedIn && m.personId) draw(m.personId);
    })
    .catch(() => {
      // Offline: the guest's record stays.
    });
}

const addPasskey = $("passkey-add") as HTMLButtonElement | null;
accountLink();
if (addPasskey) passkeyAdd(addPasskey);
if ($("device")) device();
const box = $("signin");
if (box) signIn(box);
const meRoot = $("me");
if (meRoot) me(meRoot);
if ($("your-obean")) yourObean();
const handBox = $("hand");
if (handBox) handPicker(handBox);
if ($("pass")) pass();

const form = document.getElementById("contact") as HTMLFormElement | null;
if (form) contact(form);
const q = document.getElementById("q") as HTMLInputElement | null;
if (q) help(q);
const sum = $("sum");
if (sum) status(sum);
const dailyPanel = $("daily");
if (dailyPanel) daily(dailyPanel);
const boardLines = [...document.querySelectorAll<HTMLElement>("[data-board]")];
if (boardLines.length > 0) board(boardLines);

// ── The game board (#377) on the games page and each game's page: badges and plays this week,
// from /api/board. The pages are built once; the board changes daily, so it's filled in here.
function board(lines: HTMLElement[]) {
  void loadBoard().then((b) => {
    if (!b) return;
    for (const line of lines) {
      const id = line.dataset["board"] ?? "";
      const badges = badgesOf(b, id).map((text) =>
        Object.assign(document.createElement("b"), { className: "board-badge", textContent: text }),
      );
      const plays = playsLine(b, id);
      if (badges.length === 0 && !plays) continue;
      line.replaceChildren(
        ...badges,
        ...(plays ? [Object.assign(document.createElement("span"), { textContent: plays })] : []),
      );
      line.hidden = false;
    }
  });
}

// ── A game's page: today's daily (P4-TANK-03). The page is built once; the day's mission is
// worked out here exactly as the app does (dailyOf), from the missions the page lists.
function daily(panel: HTMLElement) {
  const name = $("daily-mission");
  const game = panel.dataset["game"];
  let missions: [id: string, name: string][];
  try {
    missions = JSON.parse(panel.dataset["missions"] ?? "[]") as [string, string][];
  } catch {
    return;
  }
  if (!name || !game || missions.length === 0) return;
  const today = dailyOf(
    { id: game, daily: missions.map(([id]) => id), missions: [] },
    dayOf(Date.now()),
  );
  const chosen = missions.find(([id]) => id === today?.missionId);
  if (chosen) name.textContent = chosen[1];
}
